/*
 * Policies page enhancer.
 *
 * Shopify renders /policies/* URLs through a built-in `.shopify-policy__container`
 * markup (custom theme policy templates were deprecated in Horizon-based themes).
 * This script wraps that built-in markup with the redesigned shell:
 *   - title strip with Päivitetty / Lukuaika / Kysymyksiä? chips,
 *   - desktop sticky sidebar TOC,
 *   - mobile sticky bottom bar + bottom-sheet TOC,
 *   - parsed headings with anchor IDs and a copy-link affordance,
 *   - high-value sections lifted into callout cards,
 *   - bottom contact card.
 *
 * Loaded only on /policies/* URLs by layout/theme.liquid.
 */
(() => {
  const COPY = {
    sectionLabel: 'Sisällys',
    mobileEyebrow: 'Sisällys',
    metaUpdatedLabel: 'Päivitetty',
    metaReadingLabel: 'Lukuaika',
    metaCtaLabel: 'Kysymyksiä?',
    contactEmail: 'tietosuoja@herohim.com',
    contactEyebrow: 'Tietosuoja',
    contactHeading: 'Kysymyksiä?',
    contactBody:
      'Vastaamme tietosuojaa, käyttöehtoja ja palautuksia koskeviin kysymyksiin. Lähetä viesti, niin palaamme asiaan mahdollisimman pian.',
    closeLabel: 'Sulje',
    copyLinkPrefix: 'Kopioi linkki kohtaan',
    minutesSuffix: 'min',
  };

  // Per-policy contact info. Privacy uses the dedicated DPO mailbox; the
  // remaining policies route to the general contact owner referenced in
  // each page's body. Eyebrows match the page topic so the bottom CTA card
  // doesn't read "TIETOSUOJA" on the refund or terms pages.
  const PAGE_OVERRIDES = {
    '/policies/privacy-policy': { email: 'tietosuoja@herohim.com', eyebrow: 'Tietosuoja' },
    '/policies/contact-information': { email: 'info@herohim.com', eyebrow: 'Ota yhteyttä' },
    '/policies/refund-policy': { email: 'info@herohim.com', eyebrow: 'Palautukset' },
    '/policies/terms-of-service': { email: 'info@herohim.com', eyebrow: 'Käyttöehdot' },
    '/policies/subscription-policy': { email: 'info@herohim.com', eyebrow: 'Peruutuskäytäntö' },
  };

  const resolveOverrides = () =>
    PAGE_OVERRIDES[globalThis.location.pathname] || {};

  // Pages that benefit from a structured key/value rendering of the body
  // instead of the default prose treatment (each line is "Label: Value").
  const KV_PAGES = new Set(['/policies/contact-information']);

  const KV_LINE_RE = /^([\p{L}][\p{L}\s\-/]*?):\s*(.+)$/u;

  const transformToKeyValue = (proseEl) => {
    const paragraphs = Array.from(proseEl.querySelectorAll(':scope > p'));
    const dl = document.createElement('dl');
    dl.className = 'policies-page__kv';
    let consumed = 0;
    paragraphs.forEach((p) => {
      const text = p.textContent.replace(/\s+/g, ' ').trim();
      const match = KV_LINE_RE.exec(text);
      if (!match) return;
      const dt = document.createElement('dt');
      dt.textContent = match[1].trim();
      const dd = document.createElement('dd');
      // Preserve the live email link if the merchant included one (e.g. via
      // an <a> child of the original <p>); otherwise just inject text.
      const linkChild = p.querySelector('a[href]');
      if (linkChild) {
        dd.append(linkChild);
      } else {
        dd.textContent = match[2].trim();
      }
      dl.append(dt, dd);
      p.remove();
      consumed += 1;
    });
    if (consumed > 0) proseEl.prepend(dl);
  };

  const READING_WPM = 200;

  const HIGHLIGHT_KEYWORDS = [
    'yhteystied',
    'oikeute',
    'rekisteröidyn oikeu',
    'tietoturv',
    'tietojen turvallisuu',
  ];

  const UPDATED_PATTERNS = [
    /Päivitetty\s+([\d./]+)/i,
    /Päivätty\s+([\d./]+)/i,
    /Päivitetty:\s*([\d./]+)/i,
    /Updated\s+([\d./]+)/i,
  ];

  const NUMBERED_RE = /^\s*(\d+(?:\.\d+)*)\.?\s+(.+)$/;

  const ICONS = {
    calendar: '<svg viewBox="0 0 20 20" fill="none" stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"><rect x="3" y="4.5" width="14" height="12" rx="2.5"/><path d="M3 8.5h14M7 3v3M13 3v3"/></svg>',
    clock: '<svg viewBox="0 0 20 20" fill="none" stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"><circle cx="10" cy="10" r="7"/><path d="M10 6v4l2.5 2"/></svg>',
    mail: '<svg viewBox="0 0 20 20" fill="none" stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"><rect x="2.5" y="4.5" width="15" height="11" rx="2"/><path d="M3 6l7 5 7-5"/></svg>',
    chevron: '<svg viewBox="0 0 20 20" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round"><path d="M5 8l5 5 5-5"/></svg>',
    close: '<svg viewBox="0 0 20 20" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round"><path d="M5 5l10 10M15 5L5 15"/></svg>',
  };

  const ready = (fn) => {
    if (document.readyState === 'loading') {
      document.addEventListener('DOMContentLoaded', fn, { once: true });
    } else {
      fn();
    }
  };

  const slugify = (input) =>
    String(input)
      .toLowerCase()
      .normalize('NFKD')
      .replaceAll(/[̀-ͯ]/g, '')
      .replaceAll(/[^\w\s-]/g, '')
      .replaceAll(/\s+/g, '-')
      .replaceAll(/-+/g, '-')
      .replace(/^-|-$/g, '')
      .slice(0, 80);

  /*
   * Shopify's policy editor pastes Google-Docs HTML with inline `style` attrs
   * (12pt margins, Arial font-family, white-space:pre on <li>, list-style-type:disc).
   * We strip those to let design tokens own the rendering, but keep typographic
   * emphasis the merchant relied on (bold/italic in spans, since Google Docs
   * emits font-weight:bold spans rather than <strong>). Bold spans are converted
   * to <strong> so our `.policies-page__prose strong` rule applies.
   */
  const cleanInlineStyles = (proseEl) => {
    proseEl.querySelectorAll('[style]').forEach((el) => {
      const style = el.getAttribute('style') || '';
      const isBold = /font-weight\s*:\s*(bold|[6-9]00)/i.test(style);
      const isItalic = /font-style\s*:\s*italic/i.test(style);
      const isUnderlined = /text-decoration\s*:[^;]*underline/i.test(style);

      el.removeAttribute('style');

      if (el.tagName === 'SPAN' && (isBold || isItalic || isUnderlined)) {
        // Wrap span content in semantic emphasis so our prose CSS picks it up.
        let inner = document.createDocumentFragment();
        while (el.firstChild) inner.append(el.firstChild);
        if (isUnderlined) {
          const u = document.createElement('u');
          u.append(inner);
          inner = document.createDocumentFragment();
          inner.append(u);
        }
        if (isItalic) {
          const em = document.createElement('em');
          em.append(inner);
          inner = document.createDocumentFragment();
          inner.append(em);
        }
        if (isBold) {
          const strong = document.createElement('strong');
          strong.append(inner);
          inner = document.createDocumentFragment();
          inner.append(strong);
        }
        el.replaceWith(inner);
      }
    });
    // Unwrap empty spans left over (Google Docs nests spans for highlight/font-color).
    proseEl.querySelectorAll('span:not([class])').forEach((span) => {
      const parent = span.parentElement;
      if (!parent) return;
      while (span.firstChild) parent.insertBefore(span.firstChild, span);
      span.remove();
    });

    // Google Docs paste appends `<br><br>` to almost every <li> as a paragraph
    // separator, which renders as a tall empty space between bullets. Walk the
    // last-descendant chain and strip trailing whitespace + <br> elements.
    proseEl.querySelectorAll('li').forEach((li) => {
      let target = li;
      while (target?.lastChild) {
        const last = target.lastChild;
        if (last.nodeType === Node.TEXT_NODE && /^\s*$/.test(last.textContent)) {
          last.remove();
          continue;
        }
        if (last.nodeType === Node.ELEMENT_NODE && last.tagName === 'BR') {
          last.remove();
          continue;
        }
        if (
          last.nodeType === Node.ELEMENT_NODE &&
          ['P', 'SPAN', 'STRONG', 'EM', 'U'].includes(last.tagName)
        ) {
          target = last;
          continue;
        }
        break;
      }
    });
  };

  const parseNumbered = (text) => {
    const match = NUMBERED_RE.exec(text);
    if (!match) return null;
    return { num: `${match[1]}.`, label: match[2].trim() };
  };

  const buildShell = (container, titleText, contactEmail, contactEyebrow) => {
    const wrap = document.createElement('div');
    wrap.className = 'policies-page';
    wrap.dataset.policiesPage = '';
    wrap.innerHTML = `
      <div class="policies-page__shell">
        <header class="policies-page__header">
          <h1 class="policies-page__title"></h1>
          <div class="policies-page__meta" role="list">
            <span class="policies-page__meta-chip" role="listitem" data-policies-updated hidden>
              <span class="policies-page__meta-icon" aria-hidden="true">${ICONS.calendar}</span>
              <span class="policies-page__meta-text">
                <span class="policies-page__meta-eyebrow">${COPY.metaUpdatedLabel}</span>
                <span class="policies-page__meta-value" data-policies-updated-value></span>
              </span>
            </span>
            <span class="policies-page__meta-chip" role="listitem" data-policies-reading hidden>
              <span class="policies-page__meta-icon" aria-hidden="true">${ICONS.clock}</span>
              <span class="policies-page__meta-text">
                <span class="policies-page__meta-eyebrow">${COPY.metaReadingLabel}</span>
                <span class="policies-page__meta-value" data-policies-reading-value></span>
              </span>
            </span>
            <a class="policies-page__meta-chip policies-page__meta-chip--cta" role="listitem" href="mailto:${contactEmail}">
              <span class="policies-page__meta-icon" aria-hidden="true">${ICONS.mail}</span>
              <span class="policies-page__meta-text">
                <span class="policies-page__meta-eyebrow">${COPY.metaCtaLabel}</span>
                <span class="policies-page__meta-value">${contactEmail}</span>
              </span>
            </a>
          </div>
        </header>
        <div class="policies-page__layout">
          <aside class="policies-page__toc" aria-label="${COPY.sectionLabel}" data-policies-toc>
            <p class="policies-page__toc-label">${COPY.sectionLabel}</p>
            <ol class="policies-page__toc-list" data-policies-toc-list></ol>
          </aside>
          <article class="policies-page__prose" data-policies-prose></article>
        </div>
        <aside class="policies-page__contact-card" aria-label="${COPY.contactHeading}">
          <div class="policies-page__contact-inner">
            <p class="policies-page__contact-eyebrow">${contactEyebrow}</p>
            <h2 class="policies-page__contact-title">${COPY.contactHeading}</h2>
            <p class="policies-page__contact-body">${COPY.contactBody}</p>
            <a class="policies-page__contact-cta" href="mailto:${contactEmail}">
              ${ICONS.mail}
              <span>${contactEmail}</span>
            </a>
          </div>
        </aside>
      </div>
      <div class="policies-page__mobile-bar" data-policies-mobile-bar>
        <button
          class="policies-page__mobile-trigger"
          type="button"
          aria-haspopup="dialog"
          aria-controls="policies-mobile-sheet"
          aria-expanded="false"
          data-policies-mobile-trigger
        >
          <span class="policies-page__mobile-trigger-text">
            <span class="policies-page__mobile-trigger-eyebrow">${COPY.mobileEyebrow}</span>
            <span class="policies-page__mobile-trigger-current" data-policies-current-section>—</span>
          </span>
          <span class="policies-page__mobile-trigger-icon" aria-hidden="true">${ICONS.chevron}</span>
        </button>
      </div>
      <div
        class="policies-page__sheet"
        id="policies-mobile-sheet"
        role="dialog"
        aria-modal="true"
        aria-labelledby="policies-mobile-sheet-title"
        data-policies-sheet
        hidden
      >
        <button
          class="policies-page__sheet-backdrop"
          type="button"
          aria-label="${COPY.closeLabel}"
          data-policies-sheet-close
          tabindex="-1"
        ></button>
        <div class="policies-page__sheet-panel" role="document">
          <span class="policies-page__sheet-handle" aria-hidden="true"></span>
          <header class="policies-page__sheet-header">
            <h2 id="policies-mobile-sheet-title" class="policies-page__sheet-title">${COPY.sectionLabel}</h2>
            <button class="policies-page__sheet-close" type="button" data-policies-sheet-close aria-label="${COPY.closeLabel}">
              ${ICONS.close}
            </button>
          </header>
          <ol class="policies-page__sheet-list" data-policies-sheet-list></ol>
        </div>
      </div>
    `;
    wrap.querySelector('.policies-page__title').textContent = titleText;
    return wrap;
  };

  /*
   * Some policy pastes (notably the terms of service) cram the entire body
   * into a single <p> with `<br><br>` as paragraph separators — 100+ <br>
   * inside one element. Explode those into real paragraphs so the rest of
   * the pipeline (heading promotion, prose styling) can work normally.
   */
  const explodeBrParagraphs = (proseEl) => {
    Array.from(proseEl.querySelectorAll(':scope > p')).forEach((p) => {
      const brs = p.querySelectorAll(':scope > br');
      if (brs.length < 4) return;
      const nodes = Array.from(p.childNodes);
      const isBr = (n) => n?.nodeType === Node.ELEMENT_NODE && n.tagName === 'BR';
      const isWs = (n) => n?.nodeType === Node.TEXT_NODE && /^\s*$/.test(n.textContent);
      const chunks = [];
      let current = [];
      let i = 0;
      while (i < nodes.length) {
        const node = nodes[i];
        if (isBr(node)) {
          let j = i + 1;
          while (j < nodes.length && isWs(nodes[j])) j += 1;
          if (j < nodes.length && isBr(nodes[j])) {
            if (current.length > 0) {
              chunks.push(current);
              current = [];
            }
            i = j + 1;
            continue;
          }
          current.push(node);
          i += 1;
          continue;
        }
        current.push(node);
        i += 1;
      }
      if (current.length > 0) chunks.push(current);
      if (chunks.length <= 1) return;
      const fragment = document.createDocumentFragment();
      chunks.forEach((chunk) => {
        const hasText = chunk.some(
          (n) => n.nodeType !== Node.TEXT_NODE || /\S/.test(n.textContent || '')
        );
        if (!hasText) return;
        const newP = document.createElement('p');
        chunk.forEach((n) => newP.append(n));
        fragment.append(newP);
      });
      p.replaceWith(fragment);
    });
  };

  /*
   * Many policies don't use real <h*> tags. Two common merchant-paste shapes:
   *   1. `<p><strong>Label</strong></p>` — a standalone bold paragraph.
   *   2. `<p><strong>Label</strong> <br>body text…</p>` — bold label followed
   *      by a manual line break, then the body. (The refund policy uses this.)
   * Both are promoted to H3 so the TOC populates. Numbered #1 cases become H2.
   */
  const splitLabeledParagraphs = (proseEl, promoted) => {
    Array.from(proseEl.querySelectorAll(':scope > p')).forEach((p) => {
      const firstElement = p.firstElementChild;
      if (firstElement?.tagName !== 'STRONG') return;
      if (p.firstChild !== firstElement) return;
      const labelText = firstElement.textContent.trim();
      if (!labelText || labelText.length > 90) return;
      if (/[.!?](\s|$)/.test(labelText)) return;
      // Require an explicit <br> between the strong and the body — that's the
      // merchant's "this is a label" signal in Google-Docs paste. Without it,
      // we'd risk splitting "<strong>HeroHim</strong> kunnioittaa…" sentences.
      const br = p.querySelector(':scope > br');
      if (!br) return;
      if (firstElement.compareDocumentPosition(br) & Node.DOCUMENT_POSITION_PRECEDING) return;

      const heading = document.createElement('h3');
      heading.textContent = labelText.replace(/[:\s]+$/, '');

      const tail = document.createElement('p');
      let cursor = br.nextSibling;
      while (cursor) {
        const next = cursor.nextSibling;
        tail.append(cursor);
        cursor = next;
      }

      p.replaceWith(heading);
      if (tail.textContent.trim().length > 0) heading.after(tail);
      promoted.push(heading);
    });
  };

  const findHeadings = (proseEl) => {
    const headingNodes = Array.from(proseEl.querySelectorAll('h2, h3, h4'));
    if (headingNodes.length > 0) return headingNodes;
    explodeBrParagraphs(proseEl);
    const promoted = [];
    splitLabeledParagraphs(proseEl, promoted);
    Array.from(proseEl.querySelectorAll('p > strong:only-child')).forEach((strong) => {
      const text = strong.textContent.trim();
      const p = strong.parentElement;
      if (!p || p.parentElement !== proseEl) return;
      if (text.length > 90) return;
      if (/[.!?](\s|$).*\S/.test(text)) return;
      const isNumbered = !!parseNumbered(text);
      const heading = document.createElement(isNumbered ? 'h2' : 'h3');
      heading.textContent = text.replace(/[:\s]+$/, '');
      p.replaceWith(heading);
      promoted.push(heading);
    });
    return promoted;
  };

  const annotateHeadings = (headings) => {
    const usedIds = new Set();
    return headings
      .map((el, idx) => {
        const rawText = el.textContent.trim();
        const numbered = parseNumbered(rawText);
        const num = numbered ? numbered.num : '';
        const label = numbered ? numbered.label : rawText;
        if (numbered) {
          el.dataset.num = num;
          el.textContent = label;
        }
        let id = el.id || `policy-${slugify(label) || `section-${idx + 1}`}`;
        let unique = id;
        let suffix = 2;
        while (usedIds.has(unique)) {
          unique = `${id}-${suffix}`;
          suffix += 1;
        }
        el.id = unique;
        usedIds.add(unique);
        return {
          el,
          id: unique,
          num,
          label,
          level: Number.parseInt(el.tagName.slice(1), 10) || 2,
        };
      })
      .filter((entry) => entry.label.length > 0);
  };

  const addAnchorAffordance = (entry) => {
    const a = document.createElement('a');
    a.className = 'policies-page__anchor';
    a.href = `#${entry.id}`;
    a.setAttribute('aria-label', `${COPY.copyLinkPrefix} ${entry.label}`);
    a.textContent = '#';
    a.addEventListener('click', (event) => {
      event.preventDefault();
      const url = new URL(globalThis.location.href);
      url.hash = entry.id;
      const link = url.toString();
      if (globalThis.navigator?.clipboard?.writeText) {
        globalThis.navigator.clipboard.writeText(link).catch(() => {});
      }
      globalThis.history.replaceState(null, '', `#${entry.id}`);
      a.classList.add('is-copied');
      const original = a.textContent;
      a.textContent = '✓';
      globalThis.setTimeout(() => {
        a.classList.remove('is-copied');
        a.textContent = original;
      }, 1400);
    });
    entry.el.prepend(a);
  };

  const buildTocItem = (entry) => {
    const li = document.createElement('li');
    li.className = `level-${entry.level}`;
    li.dataset.targetId = entry.id;
    const a = document.createElement('a');
    a.href = `#${entry.id}`;
    a.dataset.num = entry.num;
    a.textContent = entry.label;
    li.append(a);
    return li;
  };

  const populateLists = (entries, tocList, sheetList) => {
    // Skip the first heading only when it's unnumbered AND the rest of the
    // document IS numbered — that's the privacy-policy "doc subtitle"
    // pattern. Pages where every heading is unnumbered (refund-policy,
    // shorter policies) should keep all of them in the TOC. The skipped
    // heading is also marked so CSS can demote it visually.
    const hasNumbered = entries.some((e) => e.num);
    const navEntries = entries.filter((entry, idx) => {
      if (idx === 0 && !entry.num && hasNumbered) {
        entry.el.setAttribute('data-policies-subtitle', '');
        return false;
      }
      return true;
    });
    const tocFrag = document.createDocumentFragment();
    const sheetFrag = document.createDocumentFragment();
    navEntries.forEach((entry) => {
      tocFrag.append(buildTocItem(entry));
      sheetFrag.append(buildTocItem(entry));
    });
    tocList.append(tocFrag);
    sheetList.append(sheetFrag);
    return navEntries;
  };

  const wireSmoothScroll = (list, onNavigate) => {
    list.addEventListener('click', (event) => {
      const a = event.target.closest('a[href^="#"]');
      if (!a) return;
      event.preventDefault();
      const id = decodeURIComponent(a.getAttribute('href').slice(1));
      const target = document.getElementById(id);
      if (!target) return;
      const reduceMotion = globalThis.matchMedia?.('(prefers-reduced-motion: reduce)').matches;
      target.scrollIntoView({ behavior: reduceMotion ? 'auto' : 'smooth', block: 'start' });
      globalThis.history.replaceState(null, '', `#${id}`);
      onNavigate?.(id);
    });
  };

  const setupScrollSpy = (entries, lists, currentLabelEl) => {
    const indexById = new Map(entries.map((e, i) => [e.id, i]));
    let activeId = entries[0]?.id ?? null;

    const apply = (id) => {
      if (id === activeId) return;
      activeId = id;
      lists.forEach((list) => {
        list.querySelectorAll('li.is-active').forEach((li) => li.classList.remove('is-active'));
        const next = list.querySelector(`li[data-target-id="${CSS.escape(id)}"]`);
        if (next) next.classList.add('is-active');
      });
      const entry = entries[indexById.get(id) ?? 0];
      if (currentLabelEl && entry) {
        currentLabelEl.textContent = entry.num ? `${entry.num} ${entry.label}` : entry.label;
      }
    };

    if (activeId) apply(activeId);

    const onScroll = () => {
      const probe = globalThis.innerHeight * 0.25;
      let bestId = entries[0]?.id ?? null;
      for (const entry of entries) {
        const top = entry.el.getBoundingClientRect().top;
        if (top - probe <= 0) {
          bestId = entry.id;
        } else {
          break;
        }
      }
      apply(bestId);
    };

    let ticking = false;
    const handler = () => {
      if (ticking) return;
      ticking = true;
      globalThis.requestAnimationFrame(() => {
        onScroll();
        ticking = false;
      });
    };
    globalThis.addEventListener('scroll', handler, { passive: true });
    globalThis.addEventListener('resize', handler);
  };

  const setupBottomBar = (root, headerEl) => {
    const bar = root.querySelector('[data-policies-mobile-bar]');
    if (!bar || !headerEl) return;
    const observer = new IntersectionObserver(
      (records) => {
        const record = records[0];
        bar.dataset.visible = record.isIntersecting ? 'false' : 'true';
      },
      { threshold: 0, rootMargin: '0px 0px -80% 0px' }
    );
    observer.observe(headerEl);
  };

  const setupSheet = (root) => {
    const trigger = root.querySelector('[data-policies-mobile-trigger]');
    const sheet = root.querySelector('[data-policies-sheet]');
    const closers = root.querySelectorAll('[data-policies-sheet-close]');
    if (!trigger || !sheet) return { close: () => {} };

    const focusableSelector = 'a[href], button:not([disabled]), [tabindex]:not([tabindex="-1"])';
    let lastFocused = null;

    const open = () => {
      lastFocused = document.activeElement;
      sheet.hidden = false;
      requestAnimationFrame(() => {
        sheet.dataset.open = 'true';
        trigger.setAttribute('aria-expanded', 'true');
      });
      const firstFocusable = sheet.querySelector(focusableSelector);
      firstFocusable?.focus();
      document.addEventListener('keydown', onKey);
      document.body.style.overflow = 'hidden';
    };

    const close = () => {
      sheet.dataset.open = 'false';
      trigger.setAttribute('aria-expanded', 'false');
      document.body.style.overflow = '';
      document.removeEventListener('keydown', onKey);
      const reduceMotion = globalThis.matchMedia?.('(prefers-reduced-motion: reduce)').matches;
      const finish = () => {
        sheet.hidden = true;
        if (lastFocused instanceof HTMLElement) lastFocused.focus({ preventScroll: true });
      };
      if (reduceMotion) finish();
      else globalThis.setTimeout(finish, 320);
    };

    const onKey = (event) => {
      if (event.key === 'Escape') {
        event.preventDefault();
        close();
      }
    };

    trigger.addEventListener('click', () => {
      if (sheet.dataset.open === 'true') close();
      else open();
    });
    closers.forEach((el) => el.addEventListener('click', close));

    return { open, close };
  };

  const promoteCallouts = (proseEl, entries) => {
    entries.forEach((entry, idx) => {
      const heading = entry.el;
      const headingText = entry.label.toLowerCase();
      const isHighlight = HIGHLIGHT_KEYWORDS.some((kw) => headingText.includes(kw));
      if (!isHighlight) return;
      const next = entries[idx + 1]?.el ?? null;
      const wrapper = document.createElement('div');
      wrapper.className = 'policies-page__callout';
      heading.parentElement?.insertBefore(wrapper, heading);
      let node = heading;
      while (node && node !== next) {
        const upcoming = node.nextSibling;
        wrapper.append(node);
        node = upcoming;
      }
    });
  };

  const extractUpdatedDate = (proseEl) => {
    const candidates = proseEl.querySelectorAll('p, h2, h3, h4');
    for (const el of candidates) {
      const text = el.textContent;
      for (const re of UPDATED_PATTERNS) {
        const m = re.exec(text);
        if (m) return { value: m[1], element: el };
      }
    }
    return null;
  };

  const formatReadingTime = (proseEl) => {
    const text = proseEl.textContent || '';
    const words = text.split(/\s+/).filter(Boolean).length;
    if (words === 0) return null;
    const minutes = Math.max(1, Math.round(words / READING_WPM));
    return `${minutes} ${COPY.minutesSuffix}`;
  };

  const init = () => {
    const container = document.querySelector('.shopify-policy__container');
    if (!container) {
      // Not actually a policy page (or content already rebuilt). Reveal whatever's there.
      document.documentElement.dataset.policiesHydrated = '';
      return;
    }

    const titleEl = container.querySelector('.shopify-policy__title h1') || container.querySelector('.shopify-policy__title');
    // Try the inner .rte wrapper first so we move the actual heading/paragraph
    // nodes directly into our prose container (not the .rte wrapper itself).
    const bodyEl =
      container.querySelector('.shopify-policy__body .rte') ||
      container.querySelector('.shopify-policy__body');
    if (!titleEl || !bodyEl) {
      document.documentElement.dataset.policiesHydrated = '';
      return;
    }

    const titleText = titleEl.textContent.trim();
    const overrides = resolveOverrides();
    const contactEmail = overrides.email || COPY.contactEmail;
    const contactEyebrow = overrides.eyebrow || COPY.contactEyebrow;
    const wrap = buildShell(container, titleText, contactEmail, contactEyebrow);
    container.parentNode.insertBefore(wrap, container);
    container.remove();

    const proseEl = wrap.querySelector('[data-policies-prose]');
    while (bodyEl.firstChild) proseEl.append(bodyEl.firstChild);
    cleanInlineStyles(proseEl);

    if (KV_PAGES.has(globalThis.location.pathname)) {
      transformToKeyValue(proseEl);
      wrap.dataset.policiesKv = '';
    }

    document.documentElement.dataset.policiesHydrated = '';

    const tocEl = wrap.querySelector('[data-policies-toc]');
    const tocList = wrap.querySelector('[data-policies-toc-list]');
    const sheetList = wrap.querySelector('[data-policies-sheet-list]');
    const currentLabelEl = wrap.querySelector('[data-policies-current-section]');
    const updatedChip = wrap.querySelector('[data-policies-updated]');
    const updatedValue = wrap.querySelector('[data-policies-updated-value]');
    const readingChip = wrap.querySelector('[data-policies-reading]');
    const readingValue = wrap.querySelector('[data-policies-reading-value]');
    const headerEl = wrap.querySelector('.policies-page__header');

    const updated = extractUpdatedDate(proseEl);
    if (updated) {
      updatedValue.textContent = updated.value;
      updatedChip.hidden = false;
      updated.element.style.display = 'none';
    }

    const reading = formatReadingTime(proseEl);
    if (reading) {
      readingValue.textContent = reading;
      readingChip.hidden = false;
    }

    const headingNodes = findHeadings(proseEl);
    if (headingNodes.length === 0) return;
    const entries = annotateHeadings(headingNodes);
    if (entries.length === 0) return;

    entries.forEach(addAnchorAffordance);
    const navEntries = populateLists(entries, tocList, sheetList);
    if (tocEl && navEntries.length > 0) tocEl.dataset.ready = 'true';
    if (navEntries.length > 0) wrap.dataset.policiesLayout = 'with-toc';

    promoteCallouts(proseEl, entries);

    const sheet = setupSheet(wrap);
    wireSmoothScroll(tocList);
    wireSmoothScroll(sheetList, () => sheet.close());

    if (navEntries.length > 0) {
      setupScrollSpy(navEntries, [tocList, sheetList], currentLabelEl);
      setupBottomBar(wrap, headerEl);
    }

    if (globalThis.location.hash) {
      const id = decodeURIComponent(globalThis.location.hash.slice(1));
      const target = document.getElementById(id);
      if (target) {
        globalThis.requestAnimationFrame(() => target.scrollIntoView({ block: 'start' }));
      }
    }
  };

  // Safety net: if init never runs (e.g. JS error), reveal Shopify's default markup
  // after a short delay so the page is never permanently blank.
  globalThis.setTimeout(() => {
    if (document.documentElement.dataset.policiesHydrated === undefined) {
      document.documentElement.dataset.policiesHydrated = 'fallback';
    }
  }, 1500);

  ready(init);
})();
